import { CherryAccountStatus } from '@/components/Timeline/types';
import { describeCherryStatus } from '@/services/payments';
import { DOCTOR_BIO } from '@bariendo/lib/patient-access-script';
import { Anchor, Box, Group, Loader, Text } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { ORBIT_NEEDS_ACTION } from '@/theme/colors';
import { Row } from './PatientAccessCallWidgets';
import { formatEstimateValue, formatEstimateWorking, WeightLossEstimate } from './weightLossEstimate';

/**
 * The computed rows two scripts share — the intro call and the PN consultation
 * both show the weight-loss estimate, the physician flag and the doctor bio.
 * One rendering each, so the two cards cannot drift.
 */

/** Physician for the chosen clinic. `name: null` means the clinic has none published. */
export interface ClinicPhysician {
  name: string | null;
}

/** A value with its working beside it, e.g. "~46 lb · 232 lb × 20%". */
export function ValueWithWorking({ value, working }: { value: string; working: string }) {
  return (
    <Text size="sm" fw={500} ta="right">
      {value}{' '}
      <Text component="span" size="sm" fw={400} c="dimmed">
        · {working}
      </Text>
    </Text>
  );
}

export function WeightLossEstimateRow({
  label,
  say,
  estimate,
}: {
  label: string;
  say?: string;
  estimate: WeightLossEstimate | undefined;
}) {
  return (
    <Row label={label} say={say}>
      {estimate ? (
        <ValueWithWorking value={formatEstimateValue(estimate)} working={formatEstimateWorking(estimate)} />
      ) : (
        <Text size="sm" c="dimmed" ta="right">
          Confirm the procedure first
        </Text>
      )}
    </Row>
  );
}

/**
 * Physician flag. Some clinics have no published physician and must raise a
 * do-not-name flag rather than offering a name the rep could read out. Renders
 * nothing until a location is picked.
 */
export function PhysicianFlag({ physician }: { physician: ClinicPhysician | undefined }) {
  if (!physician) return null;

  return physician.name ? (
    <Text size="xs" c="dimmed" py={4}>
      Physician{' '}
      <Text component="span" size="xs" fw={600} c="dark">
        {physician.name}
      </Text>{' '}
      — safe to name
    </Text>
  ) : (
    <Group gap={6} wrap="nowrap" py={4}>
      <IconAlertTriangle size={14} color={ORBIT_NEEDS_ACTION} style={{ flexShrink: 0 }} />
      <Text size="xs" c={ORBIT_NEEDS_ACTION}>
        No published physician at this clinic — do not name one
      </Text>
    </Group>
  );
}

/**
 * Whether Cherry already knows this patient (BAR-2604).
 *
 * Sits beside the Send-link action so the rep's next sentence can be right:
 * "already approved for $4,200" and "not signed up yet" call for opposite
 * things, and before this the card offered the same pivot either way.
 *
 * Renders nothing at all rather than "unknown" when the lookup could not run —
 * an absent row reads as "no information", while a row saying `—` reads as a
 * fact about the patient.
 */
export function CherryStatusRow({
  label,
  status,
  loading,
}: {
  label: string;
  status: CherryAccountStatus | undefined;
  loading: boolean;
}) {
  if (loading) {
    return (
      <Row label={label}>
        <Text size="xs" c="dimmed">
          Checking…
        </Text>
      </Row>
    );
  }

  const note = describeCherryStatus(status);
  if (!note) return null;

  return (
    <Row label={label}>
      <Text size="sm" fw={500} ta="right" c={note.tone === 'ok' ? 'teal' : note.tone === 'blocked' ? 'red' : 'dimmed'}>
        {note.label}
      </Text>
    </Row>
  );
}

/**
 * Cherry on one row: the account status, then the send link (BAR-2923). The PN
 * consultation's version of `CherryStatusRow` plus its Send-link action, which
 * the intro call still shows as two rows.
 *
 * The link hides once Cherry has approved the patient — there is nothing left to
 * apply for. With no status (the lookup could not run, or the caller cannot take
 * payments) the link stands alone, as it did before the status existed. No
 * external-link icon: it texts the patient, it does not open a site.
 */
export function CherryAccountRow({
  label,
  status,
  loading,
  onSend,
  sending,
  disabled,
}: {
  label: string;
  status: CherryAccountStatus | undefined;
  loading: boolean;
  onSend: () => void;
  sending: boolean;
  disabled?: boolean;
}) {
  const note = loading ? undefined : describeCherryStatus(status);
  const approved = status?.state === 'approved';

  return (
    <Row label={label}>
      <Group gap={4} wrap="nowrap" justify="flex-end">
        {loading && (
          <Text size="xs" c="dimmed">
            Checking…
          </Text>
        )}
        {note && (
          <Text
            size="sm"
            fw={500}
            ta="right"
            c={note.tone === 'ok' ? 'teal' : note.tone === 'blocked' ? 'red' : 'dimmed'}
          >
            {note.label}
            {!approved && ' ·'}
          </Text>
        )}
        {!approved && (
          <Anchor
            component="button"
            type="button"
            size="sm"
            c="green.9"
            disabled={disabled || sending}
            onClick={onSend}
          >
            <Group gap={4} wrap="nowrap">
              Send Cherry link
              {sending && <Loader size={12} />}
            </Group>
          </Anchor>
        )}
      </Group>
    </Row>
  );
}

/**
 * Doctor bio. Only a clinic with a published physician has one, so a
 * do-not-name clinic renders nothing here rather than an empty block.
 */
export function DoctorBioLines({ physician }: { physician: ClinicPhysician | undefined }) {
  const lines = physician?.name ? DOCTOR_BIO[physician.name] : undefined;
  if (!lines?.length) return null;

  return (
    <Box py={4}>
      {lines.map((line) => (
        <Text key={line} size="xs" c="dimmed">
          {line}
        </Text>
      ))}
    </Box>
  );
}
